import {
	type Message,
	type MessageReaction,
	type RealtimeChannel,
	api,
} from '@here/api'
import { createEffect, createResource, createSignal, on, untrack } from 'solid-js'
import { createStore, produce } from 'solid-js/store'
import { getChannel } from './resources/getChannel'
import { type DisplayMessage, getChannelHistory } from './resources/getChannelHistory'
import { getChannelHistoryAtId } from './resources/getChannelHistoryAtId'
import { getPaginatedChannelHistory } from './resources/getPaginatedChannelHistory'

export type ChatMode = 'latest' | 'history'

export type Pagination = { message: Message; direction: 'next' | 'previous' }

export function useChannel() {
	const [scrollTo, setScrollTo] = createSignal<{ id: string; isHighlight?: boolean }>()
	const [chatMode, setChatMode] = createSignal<'latest' | 'history'>()
	const [isLatestLoaded, setIsLatestLoaded] = createSignal(false)
	const [isOldestLoaded, setIsOldestLoaded] = createSignal(false)
	const [pagination, setPagination] = createSignal<Pagination>()
	const [focusMessageId, setFocusMessageId] = createSignal<string>()
	const [activeChannelId, setActiveChannelId] = createSignal<string | undefined>(
		undefined,
	)
	let subscription: RealtimeChannel | undefined
	const [messages, setMessages] = createStore<DisplayMessage[]>([])

	// active channel
	const [activeChannelResource] = createResource(activeChannelId, getChannel)

	// active channel history
	const [activeChannelHistoryResource, { refetch }] = createResource(
		() => ({
			activeChannelId: activeChannelId(),
			focusMessageId: focusMessageId(),
		}),
		({ focusMessageId, activeChannelId }) =>
			getChannelHistory(activeChannelId, focusMessageId),
	)
	const [paginatedChannelHistory] = createResource(
		pagination,
		getPaginatedChannelHistory,
	)

	const [activeChannelHistoryAtIdResource] = createResource(
		() => ({
			activeChannelId: activeChannelId(),
			focusMessageId: focusMessageId(),
		}),
		({ focusMessageId, activeChannelId }) =>
			getChannelHistoryAtId(activeChannelId, focusMessageId),
	)

	const latestMessageId = () => messages[0]?.id

	createEffect(() => {
		const messageHistory = activeChannelHistoryResource()
		if (!messageHistory) return

		setMessages(messageHistory)
		setIsLatestLoaded(true)
		setIsOldestLoaded(false)
		setChatMode('latest')
	})

	createEffect(
		on(activeChannelHistoryAtIdResource, () => {
			activeChannelHistoryAtIdResource.state === 'ready'
			console.log(activeChannelHistoryAtIdResource())
			const messageHistory = activeChannelHistoryAtIdResource()
			if (!messageHistory) return

			const id = untrack(() => focusMessageId())
			setIsLatestLoaded(false)
			setIsOldestLoaded(false)
			setChatMode('history')
			setMessages(messageHistory)
		}),
	)

	createEffect(() => {
		const id = activeChannelId()

		if (!id) {
			return
		}

		// Create a subscription to Supabase realtime
		subscription = api()
			.channel(`custom-insert-channel-${id}`)
			.on<Message>(
				'postgres_changes',
				{
					event: '*',
					schema: 'public',
					table: 'message',
					filter: `channel_id=eq.${id}`,
				},
				(payload) => {
					if (payload.eventType === 'INSERT') {
						const newMessage = payload.new as Message
						if (newMessage) {
							setMessages(
								produce((list) => {
									list.push(newMessage)
								}),
							)
							setScrollTo({ id: newMessage.id, behavior: 'auto', align: 'end' })
						}
					}

					if (payload.eventType === 'UPDATE') {
						const updatedMessage = payload.new as Message
						if (updatedMessage) {
							setMessages(
								produce((list) => {
									const index = list.findIndex((m) => m.id === updatedMessage.id)
									if (index !== -1) {
										list[index] = { ...list[index], ...updatedMessage }
									}
								}),
							)
						}
					}
				},
			)
			.subscribe()
	})

	const unsubscribe = () => {
		subscription?.unsubscribe()
	}

	const removeMessage = (id: string) => {
		setMessages((messages) => messages.filter((m) => m.id !== id))
	}

	const isReadOnly = () => activeChannelResource()?.is_read_only

	// TODO move this out of here
	const updateEmojiForMessage = (messageReaction?: MessageReaction) => {
		if (!messageReaction) return

		setMessages(
			(m) => {
				if (m.id !== messageReaction.message_id) return false

				const existingIndex = m.message_reaction?.findIndex(
					(r) => r.created_by === messageReaction.created_by,
				)

				// If reaction exists, update it
				if (existingIndex !== -1) {
					return true
				}

				// If not, append new reaction
				setMessages(
					(msg) => msg.id === messageReaction.message_id,
					'message_reaction',
					(reactions = []) => [...reactions, messageReaction],
				)

				// Prevent base-level update since we handled append separately
				return false
			},
			'message_reaction',
			(r) => r.created_by === messageReaction.created_by,
			() => messageReaction,
		)
	}

	const loadPreviousMessages = () => {
		// take the oldest message in the list and retrieve the 50 messages older than that

		const message = messages[0]
		setPagination({
			message,
			direction: 'previous',
		})
	}

	const loadNextMessages = () => {
		// take the newest message in the list and retrieve the 50 messages newer than that

		const message = messages[messages.length - 1]
		setPagination({
			message,
			direction: 'next',
		})
	}

	createEffect(
		on(paginatedChannelHistory, () => {
			if (paginatedChannelHistory() === undefined) {
				return
			}

			const history = paginatedChannelHistory()!
			const direction = pagination()?.direction

			if (history.length === 0) {
				if (direction === 'next') {
					setIsLatestLoaded(true)
				} else {
					setIsOldestLoaded(true)
				}

				return
			}

			if (direction === 'previous') {
				const scrollToId = messages[0].id
				// setScrollTo({ id: scrollToId, behaviour: 'smooth', block: 'start' })
				if (direction === 'previous') {
					const scrollToId = messages[0].id
					setMessages(
						produce((list) => {
							list.unshift(...history)
						}),
					)
				}
			} else if (direction === 'next') {
				const scrollToId = messages[messages.length - 1].id
				setMessages(
					produce((list) => {
						list.push(...history)
					}),
				)
			}
		}),
	)

	const resourcesReady = () => {
		return (
			activeChannelHistoryResource.state === 'ready' &&
			activeChannelHistoryAtIdResource.state === 'ready' &&
			activeChannelResource.state === 'ready'
		)
	}

	return {
		isOldestLoaded,
		isLatestLoaded,
		resourcesReady,
		scrollTo,
		setScrollTo,
		refetch,
		activeChannelHistoryResource,
		activeChannelHistoryAtIdResource,
		activeChannelResource,
		chatMode,
		focusMessageId,
		isReadOnly,
		loadNextMessages,
		loadPreviousMessages,
		messages,
		paginatedChannelHistory,
		pagination,
		removeMessage,
		setActiveChannelId,
		setFocusMessageId,
		unsubscribe,
		updateEmojiForMessage,
	}
}
